import Calendar from "react-calendar";
import { Calendar as CalendarIcon, Lock } from "lucide-react";
import { useTheme } from "src/components/layouts/contexts/ThemeContext";
import moment from "moment-timezone";
import { RestrictionVariant } from "../../common/freeTrialRestrictionModal";
import { useMemo, useState, useRef, useEffect } from "react";
import { useNonWorkingDays } from "src/hooks/useNonWorkingDays";
import "react-calendar/dist/Calendar.css";
import "./DateTimePicker.styles";
interface DatePickerProps {
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  fullWidth?: boolean;
  isDashboard?: boolean;
  lastDate?: string;
  isTrial?: boolean;
  freeUserSourceDate?: string;
  onBlockedSelection?: React.Dispatch<React.SetStateAction<boolean>>;
  setFreeTrialModalVariant?: React.Dispatch<
    React.SetStateAction<RestrictionVariant>
  >;
  highlightedDates?: string[];
  minDate?: Date;
}
// Component moved outside to prevent recreation on each render
const CustomInput = ({
  onClick,
  value,
  isDark,
  fullWidth,
}: {
  onClick?: () => void;
  value: string;
  isDark: boolean;
  fullWidth: boolean | undefined;
}) => {
  return (
    <div className="relative w-full">
      <input
        type="text"
        className={`${fullWidth ? "w-full" : "w-[144px] h-[36px]"} rounded-md border ${
          isDark
            ? "border-[#3d3d3d] bg-transparent text-white"
            : "border-gray-300 bg-white text-gray-900"
        } p-1.5 pr-10 text-sm focus:outline-none focus-visible:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/40 focus:ring-inset cursor-pointer`}
        value={value}
        onClick={onClick}
        readOnly
        placeholder="DD/MM/YYYY"
      />
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <CalendarIcon
          className={`h-4 w-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}
        />
      </div>
    </div>
  );
};
const DateTimePicker = (props: DatePickerProps) => {
  const {
    selectedDate,
    setSelectedDate,
    fullWidth,
    isDashboard,
    lastDate,
    freeUserSourceDate,
    isTrial,
    onBlockedSelection,
    setFreeTrialModalVariant,
    highlightedDates,
    minDate: propMinDate,
  } = props;
  const { isDark } = useTheme();
  const nonWorkingDays = useNonWorkingDays(
    isDashboard ? "lastSourceDate" : undefined,
  );
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const currentDate = moment.tz("Asia/Kolkata").toDate();
  const minDate =
    propMinDate ||
    moment
      .tz(currentDate, "Asia/Kolkata")
      .subtract(3, "years")
      .startOf("day")
      .toDate();
  const maxDate = lastDate
    ? moment(lastDate).tz("Asia/Kolkata").toDate()
    : currentDate;
  const isLockedDate = (date: Date) => {
    if (!isTrial || !freeUserSourceDate || !lastDate) return false;
    const trialMax = moment(freeUserSourceDate)
      .tz("Asia/Kolkata")
      .startOf("day");
    const maxAllowed = moment(lastDate).tz("Asia/Kolkata").startOf("day");
    const check = moment(date).tz("Asia/Kolkata").startOf("day");
    return check.isAfter(trialMax) && check.isSameOrBefore(maxAllowed);
  };
  const isNonWorkingDay = (date: Date) => {
    return nonWorkingDays.some((nonWorkingDay) =>
      moment(nonWorkingDay).isSame(moment(date), "day"),
    );
  };
  const isHighlightedDate = (date: Date) => {
    if (!highlightedDates || highlightedDates.length === 0) return false;
    return highlightedDates.some((highlightedDate) =>
      moment(highlightedDate).isSame(moment(date), "day"),
    );
  };
  const istValue = useMemo(
    () =>
      selectedDate
        ? moment(selectedDate).tz("Asia/Kolkata").format("DD/MM/YYYY")
        : "",
    [selectedDate],
  );
  // Handle click outside to close calendar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handleDateChange = (
    date: unknown,
    _event?: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (!date || Array.isArray(date)) return null;
    if (!(date instanceof Date)) return null;
    if (isLockedDate(date)) {
      onBlockedSelection?.(true);
      setFreeTrialModalVariant?.("dashboard");
      return;
    }
    const istDate = moment(date).tz("Asia/Kolkata").toDate();
    setSelectedDate(istDate);
    setIsCalendarOpen(false);
  };
  const tileDisabled = ({ date, view }: { date: Date; view: string }) => {
    // Only apply strict date validation for month view (individual days)
    if (view === "month") {
      const dateCheck = moment(date).tz("Asia/Kolkata").startOf("day");
      const minCheck = moment(minDate).tz("Asia/Kolkata").startOf("day");
      const maxCheck = moment(isDashboard ? maxDate : currentDate)
        .tz("Asia/Kolkata")
        .startOf("day");
      // Check if date is outside allowed range
      if (dateCheck.isBefore(minCheck) || dateCheck.isAfter(maxCheck)) {
        return true;
      }
      // Check if it's a non-working day
      if (isNonWorkingDay(date)) {
        return true;
      }
      // highlightedDates: [] = disable all dates
      if (highlightedDates !== undefined) {
        if (highlightedDates.length === 0) return true;
        return !isHighlightedDate(date);
      }
      return false;
    }
    // For year and decade views, allow navigation if any date in that period is valid
    if (view === "year") {
      // For year view (months), check if any day in that month is within range
      const monthStart = moment(date).startOf("month");
      const monthEnd = moment(date).endOf("month");
      const minCheck = moment(minDate).tz("Asia/Kolkata");
      const maxCheck = moment(isDashboard ? maxDate : currentDate).tz(
        "Asia/Kolkata",
      );
      // Allow if the month overlaps with the valid date range
      return monthEnd.isBefore(minCheck) || monthStart.isAfter(maxCheck);
    }
    if (view === "decade") {
      // For decade view (years), check if any day in that year is within range
      const yearStart = moment(date).startOf("year");
      const yearEnd = moment(date).endOf("year");
      const minCheck = moment(minDate).tz("Asia/Kolkata");
      const maxCheck = moment(isDashboard ? maxDate : currentDate).tz(
        "Asia/Kolkata",
      );
      // Allow if the year overlaps with the valid date range
      return yearEnd.isBefore(minCheck) || yearStart.isAfter(maxCheck);
    }
    return false;
  };
  const tileClassName = ({ date, view }: { date: Date; view: string }) => {
    const classes = [];
    if (isLockedDate(date)) {
      classes.push("locked-tile");
    }
    if (selectedDate && (view === "year" || view === "decade")) {
      const selectedMonth = moment(selectedDate).month();
      const selectedYear = moment(selectedDate).year();
      const tileMonth = moment(date).month();
      const tileYear = moment(date).year();
      if (view === "year" && tileMonth === selectedMonth) {
        classes.push("selected-year-month");
      }
      if (view === "decade" && tileYear === selectedYear) {
        classes.push("selected-year-month");
      }
    }

    if (view === "month") {
      const dateCheck = moment(date).tz("Asia/Kolkata").startOf("day");
      const maxCheck = moment(isDashboard ? maxDate : currentDate)
        .tz("Asia/Kolkata")
        .startOf("day");
      const todayCheck = moment().tz("Asia/Kolkata").startOf("day");
      if (
        dateCheck.isSame(maxCheck, "day") &&
        !maxCheck.isSame(todayCheck, "day")
      ) {
        classes.push("tomorrow-highlight");
      }
    }

    return classes.join(" ");
  };
  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    const locked = isLockedDate(date);
    if (locked) {
      // For month navigation (year view), show month name with lock icon
      if (view === "year") {
        const monthName = moment(date).format("MMMM");
        return (
          <div className="flex items-center justify-center gap-1">
            <span>{monthName}</span>
            <Lock
              size={12}
              strokeWidth={2}
              className={`${isDark ? "text-white/70" : "text-gray-800"}`}
            />
          </div>
        );
      }
      // For date navigation (month view), show only lock icon
      return (
        <Lock
          size={10}
          strokeWidth={2}
          className={`lock-icon ${isDark ? "text-white/70" : "text-gray-800"}`}
        />
      );
    }
    return null;
  };
  return (
    <div
      className={`relative w-full ${isDark ? "dark" : ""}`}
      ref={calendarRef}
    >
      <CustomInput
        value={istValue}
        isDark={isDark}
        fullWidth={fullWidth}
        onClick={() => setIsCalendarOpen(!isCalendarOpen)}
      />
      {isCalendarOpen && (
        <div className="absolute top-full -left-[4px] z-[9999] mt-1 w-full min-w-[300px]">
          <div
            className={`rounded-lg shadow-lg border ${
              isDark
                ? "bg-[#1e222d] border-[#3d3d3d] text-white"
                : "bg-white border-gray-200 text-gray-900"
            }`}
          >
            <div className="p-3">
              <Calendar
                onChange={(value) => handleDateChange(value)}
                value={
                  selectedDate
                    ? moment(selectedDate).tz("Asia/Kolkata").toDate()
                    : null
                }
                tileDisabled={tileDisabled}
                tileClassName={tileClassName}
                tileContent={({ date, view }) => tileContent({ date, view })}
                minDate={minDate}
                maxDate={isDashboard ? maxDate : currentDate}
                locale="en-US"
                calendarType="gregory"
                showFixedNumberOfWeeks={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default DateTimePicker;
